/**
 * Lista de Posts de Status do WhatsApp
 * 
 * Exibe status publicados, agendados e com falha
 * Permite deletar e visualizar detalhes de erro
 * 
 * @version 1.0.0
 * @release 2025-02-08
 */

import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { MediaThumbnail } from './MediaThumbnail';
import { ImageLightbox } from './ImageLightbox';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Trash2, 
  Image, 
  Video, 
  Type,
  AlertCircle,
  Timer,
  Loader2,
} from 'lucide-react';
import type { StatusPost, StatusState } from '@/hooks/useWhatsAppStatus';

interface StatusPostsListProps {
  /** Lista de posts de status */
  posts: StatusPost[];
  /** Se está carregando */
  isLoading?: boolean;
  /** Se está deletando */
  isDeleting?: boolean;
  /** Callback ao deletar um status */
  onDelete: (statusId: string) => void;
  /** Slug do provider da inbox (para verificar suporte a delete) */
  providerSlug?: string;
}

// Providers que suportam exclusão de status via API
const DELETE_SUPPORTED_PROVIDERS = ['waha'];

// Configuração visual por status
const statusConfig: Record<StatusState, { 
  icon: typeof CheckCircle2; 
  label: string; 
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
}> = {
  pending: {
    icon: Clock,
    label: 'Pendente',
    variant: 'secondary',
    className: 'text-muted-foreground',
  },
  scheduled: {
    icon: Timer,
    label: 'Agendado',
    variant: 'outline',
    className: 'text-blue-600 dark:text-blue-400',
  },
  publishing: {
    icon: Loader2,
    label: 'Publicando',
    variant: 'secondary',
    className: 'text-amber-600 animate-spin',
  },
  published: {
    icon: CheckCircle2,
    label: 'Publicado',
    variant: 'default',
    className: 'text-green-600 dark:text-green-400',
  },
  failed: {
    icon: XCircle,
    label: 'Falhou',
    variant: 'destructive',
    className: 'text-destructive',
  },
  expired: {
    icon: AlertCircle,
    label: 'Expirado',
    variant: 'secondary',
    className: 'text-muted-foreground',
  },
  deleted: {
    icon: Trash2,
    label: 'Deletado',
    variant: 'secondary',
    className: 'text-muted-foreground',
  },
};

// Ícone por tipo de status
const typeIcons: Record<string, typeof Type> = {
  text: Type,
  image: Image,
  video: Video,
};

/**
 * Item individual de status na lista
 */
function StatusPostItem({
  post,
  onDelete,
  isDeleting,
  providerSlug,
}: {
  post: StatusPost;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
  providerSlug?: string;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Estado para mostrar dialog informativo quando provider não suporta delete
  const [showDeleteNotSupported, setShowDeleteNotSupported] = useState(false);
  // Estado para controlar abertura do lightbox de preview de mídia
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const config = statusConfig[post.status];
  const StatusIcon = config.icon;
  const TypeIcon = typeIcons[post.status_type] || Type;

  // Calcula tempo restante para expiração (se publicado)
  const getExpirationInfo = () => {
    if (post.status !== 'published' || !post.expires_at) return null;
    
    const expiresAt = new Date(post.expires_at);
    const now = new Date();
    
    if (expiresAt <= now) {
      return { expired: true, text: 'Expirado' };
    }

    const hoursRemaining = Math.round((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60));
    return { 
      expired: false, 
      text: `Expira em ${hoursRemaining}h`,
      hours: hoursRemaining,
    };
  };

  const expirationInfo = getExpirationInfo();

  // Preview do conteúdo - sem limite de caracteres (line-clamp cuida da limitação visual)
  const contentPreview = post.content || post.media_caption || 'Sem legenda';

  return (
    <>
      <div
        className={cn(
          'group flex items-start gap-3 p-3 rounded-lg border transition-all overflow-hidden max-w-full',
          'hover:bg-accent/30',
          post.status === 'expired' && 'opacity-60'
        )}
      >
        {/* Thumbnail de mídia OU ícone do tipo de status */}
        {(post.status_type === 'image' || post.status_type === 'video') && post.media_url ? (
          <MediaThumbnail
            type={post.status_type}
            url={post.media_url}
            size={48}
            className="flex-shrink-0 rounded-lg cursor-pointer"
            onClick={() => setLightboxOpen(true)}
          />
        ) : (
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center',
            post.status === 'published' ? 'bg-primary/10' :
            post.status === 'scheduled' ? 'bg-accent' :
            post.status === 'failed' ? 'bg-destructive/10' :
            'bg-muted'
          )}>
            <TypeIcon className="h-5 w-5 text-muted-foreground" />
          </div>
        )}

        {/* Conteúdo */}
        <div className="flex-1 min-w-0 overflow-hidden space-y-1">
          {/* Tipo + Status */}
          <div className="flex items-center gap-2 flex-wrap overflow-hidden">
            <Badge variant={config.variant} className="text-[10px] h-5">
              <StatusIcon className={cn('h-3 w-3 mr-1', config.className)} />
              {config.label}
            </Badge>

            {/* Badge de expiração */}
            {expirationInfo && !expirationInfo.expired && (
              <Badge variant="outline" className="text-[10px] h-5">
                <Timer className="h-3 w-3 mr-1" />
                {expirationInfo.text}
              </Badge>
            )}

            {/* Badge de agendamento */}
            {post.status === 'scheduled' && post.scheduled_at && (
              <Badge variant="outline" className="text-[10px] h-5">
                {format(new Date(post.scheduled_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
              </Badge>
            )}
          </div>

          {/* Preview do conteúdo com quebra de palavras robusta */}
          <p className="text-sm text-muted-foreground break-words line-clamp-2">
            {contentPreview}
          </p>

          {/* Erro (se falhou) com quebra de palavras */}
          {post.status === 'failed' && post.error_message && (
            <p className="text-xs text-destructive break-words line-clamp-1">
              Erro: {post.error_message}
            </p>
          )}

          {/* Timestamp */}
          <p className="text-xs text-muted-foreground/70">
            {post.published_at
              ? `Publicado ${formatDistanceToNow(new Date(post.published_at), { addSuffix: true, locale: ptBR })}`
              : `Criado ${formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: ptBR })}`
            }
          </p>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => {
                  // Verifica se provider suporta delete remoto
                  const supportsRemoteDelete = DELETE_SUPPORTED_PROVIDERS.includes(providerSlug || '');
                  
                  // Se status está publicado e provider NÃO suporta delete, mostra mensagem informativa
                  if (post.status === 'published' && !supportsRemoteDelete) {
                    setShowDeleteNotSupported(true);
                  } else {
                    // Caso contrário, mostra dialog normal de confirmação
                    setShowDeleteConfirm(true);
                  }
                }}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remover</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Lightbox para preview de mídia em tela cheia */}
      {post.media_url && (
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          mediaItems={[{
            url: post.media_url,
            type: post.status_type as 'image' | 'video',
          }]}
          caption={post.media_caption}
          initialIndex={0}
        />
      )}

      {/* Dialog de confirmação de exclusão (quando provider suporta delete) */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover status?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá remover o registro do status do sistema.
              {post.status === 'published' && (
                <span className="block mt-2 text-amber-600 dark:text-amber-400">
                  Nota: O status também será removido do WhatsApp.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onDelete(post.id);
                setShowDeleteConfirm(false);
              }}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog informativo quando provider NÃO suporta exclusão remota */}
      <AlertDialog open={showDeleteNotSupported} onOpenChange={setShowDeleteNotSupported}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exclusão não disponível</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Nesta versão, a exclusão de status publicados via sistema não está disponível.
              </span>
              <span className="block text-muted-foreground">
                O status expira automaticamente após 24 horas ou você pode remover 
                manualmente pelo WhatsApp no seu celular.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowDeleteNotSupported(false)}>
              Entendi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Lista de posts de status com scroll
 */
export function StatusPostsList({
  posts,
  isLoading,
  isDeleting,
  onDelete,
  providerSlug,
}: StatusPostsListProps) {
  // Agrupa posts por status
  const publishedPosts = posts.filter(p => p.status === 'published');
  const scheduledPosts = posts.filter(p => p.status === 'scheduled');
  const failedPosts = posts.filter(p => p.status === 'failed');
  const otherPosts = posts.filter(p => 
    !['published', 'scheduled', 'failed'].includes(p.status)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <Image className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">Nenhum status</p>
        <p className="text-xs text-muted-foreground mt-1">
          Crie seu primeiro status para aparecer aqui
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-200px)]">
      <div className="space-y-4 pr-4">
        {/* Status Publicados */}
        {publishedPosts.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Publicados ({publishedPosts.length})
            </h4>
            <div className="space-y-2">
              {publishedPosts.map(post => (
                <StatusPostItem
                  key={post.id}
                  post={post}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  providerSlug={providerSlug}
                />
              ))}
            </div>
          </div>
        )}

        {/* Status Agendados */}
        {scheduledPosts.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Agendados ({scheduledPosts.length})
            </h4>
            <div className="space-y-2">
              {scheduledPosts.map(post => (
                <StatusPostItem
                  key={post.id}
                  post={post}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  providerSlug={providerSlug}
                />
              ))}
            </div>
          </div>
        )}

        {/* Status com Falha */}
        {failedPosts.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-destructive uppercase tracking-wider">
              Com Falha ({failedPosts.length})
            </h4>
            <div className="space-y-2">
              {failedPosts.map(post => (
                <StatusPostItem
                  key={post.id}
                  post={post}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  providerSlug={providerSlug}
                />
              ))}
            </div>
          </div>
        )}

        {/* Outros status */}
        {otherPosts.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Outros ({otherPosts.length})
            </h4>
            <div className="space-y-2">
              {otherPosts.map(post => (
                <StatusPostItem
                  key={post.id}
                  post={post}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  providerSlug={providerSlug}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
